<template>
  <div class="page" :class="{ embedded: embedded }">
    <div v-if="isSaving" class="saving-overlay">
      <div class="saving-card">Salvando dados...</div>
    </div>
    <div class="card" style="max-width: 800px;">
      <div class="card-header">
        <div>
          <h1 class="title">Relatorio de Ocorrido</h1>
          <p class="subtitle">Descricao do ocorrido e evidencias.</p>
        </div>
        <span class="pill">Seguranca</span>
      </div>

      <form class="form-grid" @submit.prevent>
        <div class="grid" style="grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px;">
          <div class="input-field">
            <label for="attraction">Projeto</label>
            <select id="attraction" v-model="attraction">
              <option v-for="item in projectOptions" :key="item" :value="item">{{ item }}</option>
            </select>
          </div>
          <div class="input-field">
            <label for="reportDate">Data</label>
            <input id="reportDate" v-model="reportDate" type="date" />
          </div>
        </div>

        <div class="input-field">
          <label for="description">Descricao do ocorrido</label>
          <textarea id="description" v-model="description" rows="4" placeholder="Descreva o ocorrido"></textarea>
        </div>

        <div class="input-field">
          <label>Imagem</label>
          <div class="image-grid">
            <div v-for="(img, index) in images" :key="`img-${index}`" class="image-preview">
              <img :src="resolveUrl(img)" alt="preview" />
            </div>
            <button v-if="images.length < 5" class="image-add" type="button" @click="triggerImageInput">+</button>
          </div>
          <input
            id="occurrence-file"
            class="hidden-input"
            type="file"
            accept="image/*"
            multiple
            @change="onImagesSelected"
          />
        </div>

        <p v-if="error" class="subtitle" style="color: #a54b2a;">{{ error }}</p>

        <div class="actions">
          <button class="btn btn-secondary" type="button" @click="saveDraft" :disabled="isSaving">
            Salvar rascunho
          </button>
          <button class="btn btn-primary" type="button" @click="send" :disabled="isSaving">
            Enviar
          </button>
          <button v-if="isEdit" class="btn btn-accent" type="button" @click="remove" :disabled="isSaving">
            Excluir
          </button>
          <button class="btn btn-ghost" type="button" @click="cancel" :disabled="isSaving">
            Cancelar
          </button>
        </div>
      </form>
    </div>
    <NoticeModal
      :open="noticeOpen"
      :title="noticeMessage"
      button-text="Entendi"
      @close="noticeOpen = false"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { navigationStore } from '@/store/navigation'
import { sessionStore } from '@/store/session'
import { submissionsStore } from '@/store/submissions'
import { newId } from '@/utils/id'
import { projectsStore } from '@/store/projects'
import { getTodayLocalISODate } from '@/utils/date'
import NoticeModal from '@/components/NoticeModal.vue'
import { uiStore } from '@/store/ui'
import { resolveUrl } from '@/utils/resolveUrl'

export default defineComponent({
  name: 'SafetyOccurrenceFormView',
  components: {
    NoticeModal
  },
  props: {
    embedded: {
      type: Boolean,
      default: false
    },
    submissionId: {
      type: String,
      default: ''
    }
  },
  emits: ['done', 'cancel'],
  setup(props, { emit }) {
    const route = useRoute()
    const router = useRouter()
    const attraction = ref('')
    const reportDate = ref(getTodayLocalISODate())
    const description = ref('')
    const images = ref<string[]>([])
    const error = ref('')
    const isSaving = ref(false)
    const noticeOpen = ref(false)
    const noticeMessage = ref('')

    const projectOptions = computed(() => {
      return projectsStore.all().map((item) => item.name).filter(Boolean)
    })

    const ensureProjectSelected = () => {
      if (isEdit.value) return
      if (attraction.value) return
      if (projectOptions.value.length > 0) {
        attraction.value = projectOptions.value[0]
      }
    }

    const triggerImageInput = () => {
      const input = document.getElementById('occurrence-file') as HTMLInputElement | null
      if (input) input.click()
    }

    const onImagesSelected = (event: Event) => {
      const target = event.target as HTMLInputElement
      const files = target.files ? Array.from(target.files) : []
      if (!files.length) return

      const availableSlots = 5 - images.value.length
      const toProcess = files.slice(0, availableSlots)

      toProcess.forEach((file) => {
        const reader = new FileReader()
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            images.value.push(reader.result)
          }
        }
        reader.readAsDataURL(file)
      })

      target.value = ''
    }

    const isEdit = ref(false)

    const loadSubmission = () => {
      const id = (props.submissionId || (route.params.id as string)) as string | undefined
      if (!id) return
      const item = submissionsStore.getById(id)
      if (!item) return
      isEdit.value = true
      attraction.value = item.attraction
      reportDate.value = item.reportDate
      description.value = item.observations?.[0] || ''
      images.value = item.activities?.[0]?.images ? [...item.activities[0].images] : []
    }

    const persist = async (status: 'rascunho' | 'enviado') => {
      if (isSaving.value) return
      error.value = ''
      if (!description.value.trim()) {
        error.value = 'Informe a descricao do ocorrido.'
        return
      }
      if (!attraction.value) {
        error.value = 'Selecione um projeto.'
        return
      }
      const user = sessionStore.state.user
      if (!user) {
        router.push('/login')
        return
      }
      const payload = {
        title: 'Relatorio de ocorrido',
        category: 'ocorrido' as const,
        attraction: attraction.value,
        reportDate: reportDate.value,
        collaborators: [],
        tools: [],
        activities: [
          {
            id: newId(),
            description: 'Fotos do ocorrido',
            images: images.value
          }
        ],
        observations: [description.value.trim()],
        reportLanguage: 'PT' as const,
        status
      }
      try {
        if (status === 'enviado') {
          isSaving.value = true
        }
      const currentId = (props.submissionId || (route.params.id as string)) as string | undefined
      if (isEdit.value && currentId) {
        await submissionsStore.updateSubmission(currentId, payload)
      } else {
        await submissionsStore.addSubmission({
          ...payload,
          author: user
        })
      }
      if (props.embedded) {
        emit('done')
      } else {
        router.push('/home')
      }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Falha ao salvar.'
        if (message.includes('Sem permissao para enviar submissao neste projeto nesta data')) {
          noticeMessage.value = 'Nao foi possivel enviar: voce nao e responsavel pelo projeto selecionado.'
          noticeOpen.value = true
          return
        }
        error.value = message
      } finally {
        isSaving.value = false
      }
    }

    const saveDraft = () => persist('rascunho')
    const send = () => persist('enviado')
    const cancel = () => {
      if (props.embedded) {
        emit('cancel')
      } else {
        navigationStore.goBack(router, '/safety')
      }
    }

    const remove = async () => {
      const currentId = (props.submissionId || (route.params.id as string)) as string | undefined
      if (!currentId) return
      if (!await uiStore.confirm('Excluir esta submissao?')) return
      await submissionsStore.deleteSubmission(currentId)
      if (props.embedded) {
        emit('done')
      } else {
        router.push('/home')
      }
    }

    onMounted(async () => {
      await projectsStore.refresh().catch(() => {
        // ignore for now
      })
      await submissionsStore.refresh().catch(() => {
        // ignore for now
      })
      ensureProjectSelected()
      loadSubmission()
    })

    return {
      attraction,
      reportDate,
      description,
      images,
      error,
      isSaving,
      noticeOpen,
      resolveUrl,
      noticeMessage,
      projectOptions,
      triggerImageInput,
      onImagesSelected,
      saveDraft,
      send,
      cancel,
      isEdit,
      remove
    }
  }
})
</script>

<style scoped lang="scss">
.page.embedded {
  min-height: auto;
  padding: 18px 16px 28px;
  align-items: flex-start;
}

.page.embedded .card {
  max-width: none !important;
  width: 100%;
  padding: 16px;
  box-shadow: none;
  background: transparent;
  border-radius: 0;
}

@media (max-width: 760px) {
  .page.embedded {
    padding: 14px 12px 22px;
  }

  .page.embedded .card {
    padding: 12px;
  }
}

.saving-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 10, 0.45);
  display: grid;
  place-items: center;
  z-index: 999;
}

.saving-card {
  background: #fff;
  padding: 18px 26px;
  border-radius: 12px;
  font-weight: 700;
}
</style>
